/**
 * Entering a group ID — the first-run gate and Settings share this (§5). The ID is checked
 * against the API *before* it's stored, so a typo says so on the spot rather than storing a
 * dead ID and dropping the player onto an access-denied screen with nothing to do about it.
 */
import { useState } from 'react';
import { parseGroupIdInput } from '@topspin/shared';
import { ApiError, fetchBootstrap } from './api.js';
import { addKey, clearKeys, getKeys, getPlayerId } from './storage.js';

export interface JoinGroupForm {
  value: string;
  setValue: (value: string) => void;
  submit: () => void;
  pending: boolean;
  error: string | undefined;
}

// One message for every rejected ID, whether it failed the format check here or the API
// didn't recognize it: neither the shape of an ID nor which of the two checks it tripped is a
// hint we hand to somebody who does not already have one (§8).
const INVALID_ID = 'Group ID is not valid. Please try again.';
const UNREACHABLE = "Couldn't reach Topspin. Check your connection and try again.";

export function useJoinGroup(opts: { replaceAll?: boolean } = {}): JoinGroupForm {
  const [value, setValue] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();

  function submit(): void {
    if (pending) return;
    const groupId = parseGroupIdInput(value);
    if (!groupId) {
      setError(INVALID_ID);
      return;
    }
    if (!opts.replaceAll && getKeys().includes(groupId)) {
      setError('This device already has that group.');
      return;
    }
    setPending(true);
    setError(undefined);
    void fetchBootstrap(groupId, getPlayerId())
      .then(() => {
        // Every stored ID was rejected, so none of them is worth keeping (§5).
        if (opts.replaceAll) clearKeys();
        addKey(groupId);
        // A reload is the simplest way to re-run bootstrap with the new ID in hand.
        window.location.reload();
      })
      .catch((err: unknown) => {
        setPending(false);
        if (!(err instanceof ApiError)) {
          setError(UNREACHABLE);
          return;
        }
        // A 401 is the API saying it doesn't know this ID — the same failure as a bad format,
        // so it reads the same. Anything else is a fault on our side, and says so.
        setError(err.status === 401 ? INVALID_ID : err.message);
      });
  }

  return {
    value,
    // Upper-cased as you type: group IDs are always shown that way, and it keeps the
    // field looking like the ID in the text message.
    setValue: (next: string) => {
      setValue(next.toUpperCase());
      setError(undefined);
    },
    submit,
    pending,
    error,
  };
}
